/**
 * =================================================================
 * CE QUI EST PARTI, DIT À L'ADMINISTRATEUR (29/10/2026)
 *
 * À la création, à la réactivation, à la modification d'email et au renvoi du
 * SMS, le serveur rend ce qu'il a VRAIMENT envoyé (`notifications`) ; l'écran,
 * lui, n'en disait qu'une phrase vague : un compte dont le SMS n'était pas parti
 * s'affichait, en vert, comme un compte créé.
 *
 * Règle : les phrases sont ici, pures, essayées au caractère près ; ce qui n'est
 * pas parti porte `ok: false` et va dans le bandeau ROUGE de la page.
 * =================================================================
 */

/** Ce que le serveur annonce dans `notifications` (contrat du 29/10). */
export interface NotificationsCompte {
  sms_sent?: boolean;
  sms_to?: string;
  sms_error?: string;
  email_sent?: boolean;
  email_to?: string;
  email_error?: string;
  set_password_link_sent?: boolean;
}

export interface MessageCompte {
  texte: string;
  ok: boolean;
}

/** La suite commune aux créations et aux modifications : ce qui est parti. */
function suiteNotifications(notif: NotificationsCompte): MessageCompte {
  // LE CANAL « LIEN » SE LIT AVANT LE CANAL SMS (29/10/2026). Quand un technicien n'a pas
  // de numéro exploitable, ou quand le serveur n'a pas de fournisseur de SMS, la création
  // passe par le lien « Définir mon mot de passe » : `creerCompteEtNotifier` rend alors
  // `set_password_link_sent: true` avec `sms_sent` resté à `false`. Tester `sms_sent ===
  // false` en premier annonçait donc, en rouge, un SMS qui n'a jamais été tenté — alors que
  // le lien, lui, était parti. Les deux canaux s'excluent : cet ordre ne change rien au cas
  // du SMS.
  if (notif.set_password_link_sent) {
    return { texte: ' Lien « Définir mon mot de passe » envoyé par courriel.', ok: true };
  }
  if (notif.sms_sent === false) {
    const raison = notif.sms_error ? ` : ${notif.sms_error}` : '';
    return {
      texte: ` Le SMS n'est pas parti${raison}. Corrigez le numéro si besoin, puis utilisez « Renvoyer le SMS ».`,
      ok: false
    };
  }
  if (notif.sms_sent === true) {
    if (notif.email_sent) {
      return { texte: " Code SMS et courriel avec le lien de l'application envoyés.", ok: true };
    }
    const raison = notif.email_error ? ` : ${notif.email_error}` : '';
    return {
      texte: ` Code SMS envoyé. Le courriel avec le lien de l'application n'est pas parti${raison}.`,
      ok: true
    };
  }
  return { texte: '', ok: true };
}

/**
 * Création (ou réactivation, ou rattachement) d'un compte mobile.
 * `nom` = celui que la page affiche pour ce technicien.
 */
export function messageCreation(
  action: string | undefined,
  nom: string,
  notif: NotificationsCompte = {}
): MessageCompte {
  const debut =
    action === 'reactivated'
      ? `Compte réactivé pour ${nom}.`
      : action === 'linked'
        ? `Compte créé et rattaché au profil existant de ${nom}.`
        : `Compte créé pour ${nom}.`;
  const suite = suiteNotifications(notif);
  return { texte: debut + suite.texte, ok: suite.ok };
}

/** Modification de l'email d'un technicien : même forme, autre début. */
export function messageModificationEmail(nom: string, notif: NotificationsCompte = {}): MessageCompte {
  const suite = suiteNotifications(notif);
  return { texte: `Email modifié pour ${nom}.` + suite.texte, ok: suite.ok };
}

/**
 * Renvoi du SMS : la réponse ne se lit qu'en cas de succès (le serveur a
 * répondu 200), d'où une simple phrase. `sms_to` est le numéro normalisé, en
 * entier, pour qu'un indicatif faux (+590 au lieu de +596) se voie.
 */
export function messageRenvoiSms(notif: NotificationsCompte = {}): string {
  let texte = `SMS envoyé au ${notif.sms_to}.`;
  if (notif.email_sent) {
    texte += ` Courriel avec le lien de l'application envoyé à ${notif.email_to}.`;
  } else if (notif.email_error) {
    texte += ` Le courriel n'est pas parti : ${notif.email_error}.`;
  }
  return texte;
}
